import { styled } from '@linaria/react';
import { Group } from '@mantine/core';
import { Controls, ReactFlow } from '@xyflow/react';
import { memo, useMemo } from 'react';
import type { GraphData, ProcessMapping } from '../../../api/process-client/process-client.type';
import { Stages } from '../../../api/process-client/process-client.type';
import { useTranslation } from 'react-i18next';
import { useStatusLabelResolver } from '../../../contexts/status-labels.context';
import { useProcessSelector } from '../../../store/process-store/process-store.hooks';
import { newCOLORS } from '../../../styles/colors';
import { baseBlack, secondaryBase } from '../../../styles/design-tokens';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import type { ProcessScope } from '../context/process.context.type';
import { StageEdge } from '../edges/stage-edge';
import { WorkflowEdgeIn, WorkflowEdgeOut } from '../edges/workflow-edge';
import { StageNode } from '../nodes/stage-node';
import { WorkflowNode } from '../nodes/workflow-node';
import { ProcessGraphLegend } from '../process-graph-legend';
import { ViewType } from '../process.type';
import { getScopedSubprojectIds } from '../process.helpers';
import { calculateGraph } from '../processv2.helpers';
import { StyledSwitch, ViewToggle } from '../scope/view-toggle';
import { WorkflowViewToggle } from '../scope/workflow-view-toggle';
import { stringifyProcessScope } from './process-graph.helpers';

const nodeTypes = {
  stageNode: StageNode,
  workflowNode: WorkflowNode,
};

const edgeTypes = {
  stageEdge: StageEdge,
  workflowEdgeIn: WorkflowEdgeIn,
  workflowEdgeOut: WorkflowEdgeOut,
};

type Props = {
  graphData: GraphData | undefined;
  mapping: ProcessMapping | null;
  stages: Stages[];
  viewType: ViewType;
  scope: ProcessScope;
  setScope: (scope: Partial<ProcessScope>) => void;
  currencyCode: string;
  isRightGraph?: boolean;
};
export const SectionGraph = memo(function SectionGraph({
  graphData,
  mapping,
  viewType,
  scope,
  setScope,
  stages,
  currencyCode,
  isRightGraph = false,
}: Props) {
  const i18n = useTranslation();
  const { board, useWorkflow, useGroups } = scope;
  const workflowLabel = i18n.t('data_management.workflows.workflow');

  const isComparisonView = useProcessSelector((state) => state.isComparisonView);

  // Scoped to this graph's own boards rather than a shared provider: in the
  // comparison view the two sides can be on different scopes, and each should
  // render its own status casing. Derived from the whole scope, not just
  // `scope.board` — a workflow- or team-scoped graph has no single board.
  const scopedSubprojectIds = useMemo(() => getScopedSubprojectIds(scope), [scope]);
  const { getStatusLabel: resolveStatusLabelForDisplay } = useStatusLabelResolver(scopedSubprojectIds);

  const { nodes, edges } = useMemo(
    () =>
      calculateGraph(
        graphData,
        mapping,
        stages as Stages[],
        viewType,
        useGroups,
        isRightGraph,
        currencyCode,
        resolveStatusLabelForDisplay,
      ),
    [graphData, mapping, stages, viewType, useGroups, isRightGraph, currencyCode, resolveStatusLabelForDisplay],
  );

  const flowProps = useMemo(
    () => ({
      nodeOrigin: [0.5, 0.5] as [number, number],
      fitView: true,
      nodeTypes,
      edgeTypes,
      nodes,
      edges,
      proOptions: { hideAttribution: true },
      edgesFocusable: false,
      elementsSelectable: false,
    }),
    [nodes, edges],
  );

  return (
    <>
      <Group justify="space-between">
        <Text size="tiny" weight="medium">
          {stringifyProcessScope(scope, isComparisonView)}
        </Text>
        <Group gap="xs">
          <Tooltip
            variant="black"
            label={
              !useWorkflow && !board
                ? `Select a ${workflowLabel} or ${i18n.t('common.board')} to view without groups.`
                : `Toggle to view with or without ${workflowLabel.toLowerCase()} groups`
            }
          >
            <Group gap="xs">
              <Text size="tiny">Use Groups</Text>

              <StyledSwitch
                checked={scope.useGroups}
                onChange={(event: any) => setScope({ useGroups: event.currentTarget.checked })}
                color={secondaryBase}
                disabled={!useWorkflow && !board}
              />
            </Group>
          </Tooltip>

          <Text size="tiny">Full View</Text>
          {mapping &&
            (useWorkflow ? (
              <WorkflowViewToggle mapping={mapping} useGroups={scope.useGroups} isRightGraph={isRightGraph} />
            ) : (
              <ViewToggle mapping={mapping} isRightGraph={isRightGraph} />
            ))}
        </Group>
      </Group>
      <StyledReactFlow {...flowProps}>
        <Controls position="bottom-right" showInteractive={false} />
        <ProcessGraphLegend
          legendItems={[
            {
              icon: 'square',
              color: baseBlack,
              text: i18n.t('process.section_graph.legend.expected_steps'),
              variant: 'filled',
            },
            {
              icon: 'square',
              color: newCOLORS.purple2,
              text: i18n.t('process.section_graph.legend.skipped_steps'),
              variant: 'filled',
            },
            {
              icon: 'square',
              color: newCOLORS.purple2,
              text: i18n.t('process.section_graph.legend.backwards_steps'),
              variant: 'outlined',
            },
            {
              icon: 'square',
              color: newCOLORS.gray3,
              text: i18n.t('process.section_graph.legend.blocked_steps'),
              variant: 'filled',
            },
          ]}
          graphData={graphData}
          showOverview={isComparisonView}
          resolvedLabels={mapping?.labels ?? undefined}
        />
      </StyledReactFlow>
    </>
  );
});

const StyledReactFlow = styled(ReactFlow)`
  .react-flow__handle {
    opacity: 0;
    border: none;
    min-height: 0;
  }

  .react-flow__controls {
    background: #f5f5f5;
    box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.3);
  }

  .react-flow__edge-path {
    stroke: #000;
  }
`;
